<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import { Alert, Container, Input, Select, Text, Token } from '@Shelly/shelly-design-system';
	//Helpers
	import { capFirstChar, getErrorOrInfoMessageForMinAndMaxUnit } from 'src/helpers/utilities';
	import CustomDynamicInput from 'src/components/CustomDynamicInput.svelte';
	import { getCurySlotOptions } from 'src/helpers/actions/local/devices/cury_local_actions';
	import { getIlluminationIcon } from 'src/helpers/device_data_helpers';
	import { clearQuotes } from 'src/helpers/actions/webhooks/conditions/webhook_conditions_helpers';
	import { getPresenceZoneOptions } from 'src/helpers/actions/local/ui/presence_local_actions_view';

	export let eventsOptions: any[],
		eventsIndex = -1,
		error: { show: boolean; label: string };
</script>

{#if eventsOptions[eventsIndex]?.conditions?.length}
	<Container type="borderDashed" class="mt-m" padding="m">
		{#each eventsOptions[eventsIndex].conditions as event, i}
			{#if eventsOptions[eventsIndex]?.customExprNotSet && /(xtotal|xpercent|xvoltage|xfreq)/.test(event.conditions?.[event.conditionsIndex]?.value)}
				<Alert
					type="warning"
					isHeadingBold
					heading={$format('label_info', { default: 'Information' })}
					class="mb-s"
					message={$format('custom_expression_is_not_set.', {
						default:
							'No custom expression is set. The value will evaluate from the default input values!',
					})}
				/>
			{/if}

			{#if event.type == 'cury' || event.type == 'em' || event.type == 'em1' || event.type == 'pm1'}
				<Text element="h2" class="mb-s">
					{capFirstChar(
						$format('em_' + event.attrName + '_label_condition', {
							default: event.attrName.replace(/[._]/g, ' '),
						}),
					)}
					{$format('condition_is', {
						default: 'is',
					})}
				</Text>
			{:else}
				<Text element="h2" class="mb-s text-capitalize">
					{$format('label_additional_condition', {
						default: 'Additional condition',
					})}
				</Text>
			{/if}

			<div class="d-flex flex-wrap" style="gap: var(--spacing-s);">
				<Token
					id="token-condition-any"
					size="m"
					title={$format('condition_any', { default: 'Any' })}
					selected={event.conditionsIndex == -1}
					on:click={() => {
						event.conditionsIndex = -1;
					}}
				/>
				{#each event.conditions as condition}
					<Token
						id={`token-event-${condition.index}`}
						size="m"
						title={capFirstChar(condition.title)}
						selected={event.conditionsIndex === condition.index}
						on:click={() => {
							event.conditionsIndex = condition.index;
						}}
					/>
				{/each}
			</div>
			{#if event.attrName == 'phase'}
				<Select
					id="select-tn854yb"
					aria-label={$format('select_component_phase', {
						default: 'Select phase',
					})}
					class="mt-m"
					size="m"
					value={event.phases.find((i) => i.value == event.conditionValue)}
					disabled={event.conditionsIndex == -1}
					options={event.phases}
					icon={'fas fa-list'}
					on:change={(e) => {
						event.conditionValue = e.detail['value'];
					}}
				/>
			{:else if event.attrName == 'idx' && event.buttons}
				<Select
					id="select-fpezl4i"
					aria-label={$format('select_component_value', {
						default: 'Select value',
					})}
					class="mt-m"
					size="m"
					value={event.buttons.find((i) => i.value == event.conditionValue)}
					disabled={event.conditionsIndex == -1}
					options={event.buttons}
					icon={'fas fa-list'}
					on:change={(e) => {
						event.conditionValue = e.detail['value'];
					}}
				/>
			{:else if event.type === 'presencezone' && event.attrName == 'state' && event.conditionsIndex >= 0}
				{@const zoneOptions = getPresenceZoneOptions()}
				<div class="d-flex flex-wrap">
					{#each zoneOptions as item}
						<Token
							id={`token-state-${i}`}
							size="m"
							class="mt-m mr-s"
							title={item.title}
							selected={item.value == event.conditionValue}
							on:click={() => {
								event.conditionValue = item.value;
							}}
						/>
					{/each}
				</div>
			{:else if event.enums}
				<Select
					id="select-dvr7ok9"
					aria-label={$format('select_component_value', {
						default: 'Select value',
					})}
					class="mt-m"
					size="m"
					value={event.enums.find((i) => String(i.value) === String(event.conditionValue)) ||
						event.enums[0]}
					disabled={event.conditionsIndex == -1}
					options={event.enums}
					icon={event.enums.find((i) => String(i.value) === String(event.conditionValue))
						?.icon ||
						event.enums[0]?.icon ||
						'fas fa-list'}
					on:change={(e) => {
						event.conditionValue = e.detail['value'];
					}}
				/>
			{:else if event.type === 'cury' && event.attrName === 'slot' && event.conditionsIndex >= 0}
				{@const slotOptions = getCurySlotOptions()}
				<Select
					id="select-1j6y2v3"
					aria-label={$format('select_component_slot', {
						default: 'Select slot',
					})}
					class="mt-s width-md-l width-sm-full"
					size="m"
					value={slotOptions.find((i) => i.value == clearQuotes(event.conditionValue)) ||
						slotOptions[0]}
					options={slotOptions}
					icon="fas fa-list"
					on:change={(e) => {
						const value = e.detail['value'];
						event.conditionValue = `"${value}"`;
					}}
				/>
			{:else if event.conditionsIndex >= 0}
				{#if /^(number)$/.test(eventsOptions[eventsIndex]?.type)}
					{@const minMaxInfo = getErrorOrInfoMessageForMinAndMaxUnit(
						event.conditionValue,
						eventsOptions[eventsIndex]?.config?.min,
						eventsOptions[eventsIndex]?.config?.max,
						eventsOptions[eventsIndex]?.config?.meta?.ui?.unit,
					)}
					<CustomDynamicInput
						additionalContainerClass="mt-m"
						additionalInputAttributes={{
							icon: eventsOptions[eventsIndex]?.icon || 'fa fa-calculator',
						}}
						minusFunction={() => {
							event.conditionValue = Number(event.conditionValue) - 1;
						}}
						disabledMinusBtn={Number(event.conditionValue) <= 0}
						plusFunction={() => {
							event.conditionValue = Number(event.conditionValue) + 1;
						}}
						label={event.conditions?.[event.conditionsIndex]?.label || event.label}
						value={Number(event.conditionValue)}
						info={minMaxInfo.info}
						error={minMaxInfo.error}
						inputHandler={(e) => {
							event.conditionValue = e.target['value'];
						}}
					/>
				{:else if event.attrName == 'illumination'}
					<div class="d-flex flex-wrap">
						{#each ['dark', 'twilight', 'bright'] as item}
							<Token
								id="token-0qicyfz"
								size="m"
								class="mt-m mr-s"
								title={capFirstChar(
									$format(`alarm_state_${item}`, {
										default: item,
									}),
								)}
								icon={`fas ${getIlluminationIcon(item)}`}
								selected={item === clearQuotes(event.conditionValue)}
								on:click={() => {
									event.conditionValue = `"${item}"`;
								}}
							/>
						{/each}
					</div>
				{:else if /^(text|enum)$/.test(eventsOptions[eventsIndex]?.type)}
					<Input
						id="input-0qicyfz"
						aria-labelledby="input-0qicyfz"
						size="m"
						class="mt-s width-md-l width-sm-full"
						value={clearQuotes(event.conditionValue)}
						label={event.conditions?.[event.conditionsIndex]?.label || event.label}
						error={error.show && event.conditionValue == '' ? error.label : ''}
						icon={eventsOptions[eventsIndex]?.icon || 'fa fa-pen'}
						tabIndex="-1"
						on:input={(e) => {
							const value = e.target['value'];
							event.conditionValue = `"${value}"`;
						}}
					/>
				{:else}
					<CustomDynamicInput
						additionalContainerClass="mt-s"
						additionalInputAttributes={{
							icon: eventsOptions[eventsIndex]?.icon || 'fa fa-calculator',
						}}
						minusFunction={() => {
							event.conditionValue = Number(event.conditionValue) - 1;
						}}
						disabledMinusBtn={Number(event.conditionValue) <= 0}
						plusFunction={() => {
							event.conditionValue = Number(event.conditionValue) + 1;
						}}
						value={Number(event.conditionValue)}
						error={null}
						inputHandler={(e) => {
							event.conditionValue = e.target['value'];
						}}
					/>
				{/if}
			{/if}
			{#if i < eventsOptions[eventsIndex].conditions?.length - 1}
				<div class="mb-m"></div>
			{/if}
		{/each}
	</Container>
{/if}
